import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import API_CONFIG from '../config/api.js';

const HealthCheck = () => {
  const [healthData, setHealthData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(false);

  const checkHealth = async () => {
    setLoading(true);
    setError(null);

    try {
      // Try both health endpoints
      let response;
      try {
        response = await axios.get(`${API_CONFIG.BASE_URL}/health`, {
          timeout: 5000 // 5 second timeout
        });
      } catch (healthErr) {
        // If health endpoint fails, try ping
        response = await axios.get(`${API_CONFIG.BASE_URL}/ping`, {
          timeout: 3000 // 3 second timeout
        });
      }

      setHealthData(response.data);
      setLoading(false);
    } catch (err) {
      setError('API server is unreachable');
      setLoading(false);
      toast.error('Failed to check API health: ' + err.message);
    }
  };

  useEffect(() => {
    checkHealth();
  }, []);

  const getStatusColor = () => {
    if (error) return 'bg-red-500';
    if (!healthData) return 'bg-gray-500';
    
    // If we have full health data with database info
    if (healthData.database) {
      return healthData.database.statusCode === 1 
        ? 'bg-green-500' // connected
        : healthData.database.statusCode === 2 
          ? 'bg-yellow-500' // connecting 
          : 'bg-red-500'; // disconnected or disconnecting
    }
    
    // If we only have ping data, show as yellow (partial health)
    return healthData.message === 'pong' ? 'bg-yellow-500' : 'bg-red-500';
  };

  const getStatusText = () => {
    if (error) return 'Unreachable';
    if (!healthData) return 'Unknown';
    
    if (healthData.database) {
      return `DB: ${healthData.database.status}`;
    }
    
    return healthData.message === 'pong' ? 'Service OK' : 'Service Error';
  };

  return (
    <div className="mb-4 p-4 bg-white shadow rounded-lg">
      <div className="flex justify-between items-center">
        <h2 className="text-lg font-semibold">Backend Health Status</h2>
        <div className="flex space-x-2">
          <div className="flex items-center">
            <div className={`w-3 h-3 rounded-full mr-2 ${getStatusColor()}`}></div>
            <span className="text-sm font-medium">{getStatusText()}</span>
          </div>
          <button 
            onClick={checkHealth}
            disabled={loading}
            className="px-2 py-1 bg-primary-600 text-white text-sm rounded hover:bg-primary-700"
          >
            {loading ? 'Checking...' : 'Refresh'}
          </button>
          <button 
            onClick={() => setExpanded(!expanded)}
            className="px-2 py-1 bg-gray-200 text-gray-800 text-sm rounded hover:bg-gray-300"
          >
            {expanded ? 'Hide Details' : 'Show Details'}
          </button>
        </div>
      </div>

      {expanded && (
        <div className="mt-4 p-3 bg-gray-100 rounded text-sm font-mono">
          {loading ? (
            <div className="flex justify-center items-center h-20">
              <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-primary-600"></div>
            </div>
          ) : error ? (
            <div className="text-red-600">
              {error}
              <div className="mt-2">
                <p>Please check:</p>
                <ul className="list-disc ml-5">
                  <li>Backend server is running</li>
                  <li>API URL is correctly configured</li>
                  <li>Network connectivity</li>
                  <li>CORS settings on the server</li>
                </ul>
              </div>
            </div>
          ) : (
            <pre className="whitespace-pre-wrap break-words overflow-x-auto max-w-full">
              {JSON.stringify(healthData, null, 2)}
            </pre>
          )}
        </div>
      )}
    </div>
  );
};

export default HealthCheck; 